/**
 * Pricing engine — the single implementation of the inoRain OTT cost model.
 *
 * Every surface (the expert calculator, the guided wizard, the customer quote
 * view, the PDF proposal) consumes this module, so a pricing change lands in
 * one place and cannot drift between views.
 *
 * A "quote state" is the plain object described by DEFAULT_QUOTE_STATE below.
 * It is the unit that gets saved, shared by link, and re-costed on read.
 */
import APP_CONFIG, { TIER_ORDER, isIncludedInTier } from '../config/appConfig';

/** Peak-concurrency to active-subscriber ratios, by content type. */
export const SUBSCRIBER_RATIOS = {
    consumer: { ratio: 11, name: 'Consumer Streaming', description: 'General entertainment (Netflix-style)' },
    educational: { ratio: 17, name: 'Educational Content', description: 'Online courses, training platforms' },
    live: { ratio: 6, name: 'Live Events', description: 'Sports, concerts, live broadcasts' },
    mixed: { ratio: 8, name: 'Mixed Content', description: 'Combination of live and on-demand' }
};

export const DEFAULT_QUOTE_STATE = {
    // Catalogue and audience
    videoAssets: APP_CONFIG.defaults.videoAssets,
    avgDuration: APP_CONFIG.defaults.avgDuration,
    totalPlays: APP_CONFIG.defaults.totalPlays,
    avgWatchTime: APP_CONFIG.defaults.avgWatchTime,
    videoQuality: APP_CONFIG.defaults.videoQuality,
    subscriberEstimationType: 'consumer',

    // What they are buying
    selectedPlatforms: { ...APP_CONFIG.defaults.selectedPlatforms },
    selectedFeatures: { ...APP_CONFIG.defaults.selectedFeatures },

    // Usage-based add-ons
    ssaiChannels: APP_CONFIG.defaults.ssaiChannels,
    ssaiBandwidthTB: APP_CONFIG.defaults.ssaiBandwidthTB,
    aiDubbingMinutes: APP_CONFIG.defaults.aiDubbingMinutes,
    aiSubtitleMinutes: APP_CONFIG.defaults.aiSubtitleMinutes,
    pricingMode: APP_CONFIG.defaults.pricingMode,
    appDiscount: APP_CONFIG.defaults.appDiscount,

    // Revenue model
    revenueModel: APP_CONFIG.defaults.revenueModel,
    subscribers: APP_CONFIG.defaults.subscribers,
    avgSpend: APP_CONFIG.defaults.avgSpend,
    impressions: APP_CONFIG.defaults.impressions,
    cpm: APP_CONFIG.defaults.cpm,
    transactions: APP_CONFIG.defaults.transactions,
    avgPrice: APP_CONFIG.defaults.avgPrice,

    // Presentation
    billingType: APP_CONFIG.defaults.billingType,
    currency: APP_CONFIG.defaults.currency,
    monthlyGrowthRate: APP_CONFIG.defaults.monthlyGrowthRate
};

/** Approx. active subscribers implied by peak concurrency and content type. */
export const estimateActiveSubscribers = (totalPlays, subscriberEstimationType) => {
    const entry = SUBSCRIBER_RATIOS[subscriberEstimationType] || SUBSCRIBER_RATIOS.consumer;
    return Math.round((Number(totalPlays) || 0) * entry.ratio);
};

/**
 * Bandwidth and storage the workload needs, in TB.
 *
 * Storage = assets x hours each x GB/hour x overhead.
 * Bandwidth = peak concurrency x watch hours x bitrate across the month,
 * adjusted for network inefficiency and peak concurrency.
 */
export const calculateRequirements = (state, calculationSettings) => {
    // Falls back to the compiled defaults when no stored settings are supplied,
    // so every existing caller keeps working.
    const cs = calculationSettings || APP_CONFIG.calculationSettings;
    const { videoAssets, avgDuration, totalPlays, avgWatchTime, videoQuality } = state;

    const streamingBitrate = cs.streamingBitrates[videoQuality]?.value || cs.streamingBitrates.MIXED.value;
    const storagePerHour = cs.storageRequirements[videoQuality]?.value || cs.storageRequirements.MIXED.value;
    const bandwidthEfficiency = cs.efficiencyFactors.bandwidthEfficiency.value;
    const peakUsageFactor = cs.efficiencyFactors.peakUsageFactor.value;
    const storageOverhead = cs.efficiencyFactors.storageOverhead.value;

    const contentStorageGB = (videoAssets || 0) * ((avgDuration || 0) / 60) * storagePerHour * storageOverhead;

    const hoursInMonth = 30 * 24;
    const baseBandwidthGB = (totalPlays || 0) * ((avgWatchTime || 0) / 60) * streamingBitrate * hoursInMonth * 0.125;
    const monthlyBandwidthGB = baseBandwidthGB * (1 / bandwidthEfficiency) * peakUsageFactor;

    return {
        storage: contentStorageGB / 1000,
        bandwidth: monthlyBandwidthGB / 1000
    };
};

/** Monthly revenue implied by the selected business model. */
export const calculateRevenue = (state) => {
    const { revenueModel, subscribers, avgSpend, impressions, cpm, transactions, avgPrice } = state;
    let total = 0;
    if (revenueModel === 'SVOD' || revenueModel === 'HYBRID') total += (subscribers || 0) * (avgSpend || 0);
    if (revenueModel === 'AVOD' || revenueModel === 'HYBRID') total += ((impressions || 0) / 1000) * (cpm || 0);
    if (revenueModel === 'TVOD' || revenueModel === 'HYBRID') total += (transactions || 0) * (avgPrice || 0);
    return total;
};

/**
 * Usage-based add-ons: SSAI channels and per-minute AI processing.
 *
 * These are never bundled with a tier, so the figure is identical on every tier
 * and is computed once. Channel bandwidth bills at its own per-TB rate rather
 * than the per-GB capacity overage rate.
 */
export const calculateUsageAddOns = (state) => {
    const u = APP_CONFIG.usageAddOns;
    const channels = Number(state.ssaiChannels) || 0;
    const channelTB = Number(state.ssaiBandwidthTB) || 0;
    const dubbingMinutes = Number(state.aiDubbingMinutes) || 0;
    const subtitleMinutes = Number(state.aiSubtitleMinutes) || 0;

    const items = [];

    const ssaiChannelCost = channels * u.ssaiChannelBuilder.unitPrice;
    if (channels > 0) {
        items.push({
            key: 'ssaiChannelBuilder',
            name: u.ssaiChannelBuilder.name,
            detail: channels + ' channel' + (channels === 1 ? '' : 's'),
            cost: ssaiChannelCost
        });
    }

    const ssaiBandwidthCost = channelTB * u.ssaiChannelBuilder.bandwidthPricePerTB;
    if (channelTB > 0) {
        items.push({
            key: 'ssaiBandwidth',
            name: 'Channel bandwidth',
            detail: channelTB + ' TB at $' + u.ssaiChannelBuilder.bandwidthPricePerTB + '/TB',
            cost: ssaiBandwidthCost
        });
    }

    const dubbingCost = dubbingMinutes * u.aiDubbing.unitPrice;
    if (dubbingMinutes > 0) {
        items.push({
            key: 'aiDubbing',
            name: u.aiDubbing.name,
            detail: dubbingMinutes.toLocaleString() + ' min',
            cost: dubbingCost
        });
    }

    const subtitleCost = subtitleMinutes * u.aiSubtitles.unitPrice;
    if (subtitleMinutes > 0) {
        items.push({
            key: 'aiSubtitles',
            name: u.aiSubtitles.name,
            detail: subtitleMinutes.toLocaleString() + ' min',
            cost: subtitleCost
        });
    }

    return {
        items,
        ssaiChannelCost,
        ssaiBandwidthCost,
        dubbingCost,
        subtitleCost,
        total: ssaiChannelCost + ssaiBandwidthCost + dubbingCost + subtitleCost
    };
};

/**
 * Cost of one tier for this quote state.
 *
 * The same basket of platforms and features costs a different amount on each
 * tier, because each tier already bundles a different slice of it. That gap is
 * the whole economics of the tier ladder, so it is computed per tier rather
 * than once.
 */
export const calculateTierCosts = (tierKey, state, requirements, activeSubscribers, usage) => {
    const packages = APP_CONFIG.packages;
    const platforms = APP_CONFIG.platforms;
    const features = APP_CONFIG.features;
    const overage = APP_CONFIG.overage;

    const pkg = packages[tierKey];
    const isCustom = !!pkg.custom;
    const isLegacy = state.pricingMode === 'legacy';
    const baseCost = pkg.price || 0;

    // Platform add-ons: selected, but not bundled with this tier.
    const platformAddOnItems = [];
    let platformAddOnCost = 0;
    Object.entries(state.selectedPlatforms || {}).forEach(([key, on]) => {
        const platform = platforms[key];
        if (!on || !platform || platform.price <= 0) return;
        if (isIncludedInTier(platform, tierKey)) return;
        platformAddOnItems.push({ key, name: platform.name, price: platform.price });
        platformAddOnCost += platform.price;
    });

    // Feature add-ons: selected, but not bundled with this tier.
    const featureAddOnItems = [];
    let featureAddOnCost = 0;
    Object.entries(state.selectedFeatures || {}).forEach(([key, on]) => {
        const feature = features[key];
        if (!on || !feature || feature.core || feature.price <= 0) return;
        if (isIncludedInTier(feature, tierKey)) return;
        featureAddOnItems.push({ key, name: feature.name, price: feature.price });
        featureAddOnCost += feature.price;
    });

    // Capacity beyond the tier allowance is billed per GB.
    const bandwidthOverGB = isCustom ? 0 : Math.max(0, (requirements.bandwidth - pkg.bandwidth) * 1000);
    const storageOverGB = isCustom ? 0 : Math.max(0, (requirements.storage - pkg.storage) * 1000);
    const bandwidthOverage = bandwidthOverGB * overage.bandwidth.pricePerGB;
    const storageOverage = storageOverGB * overage.storage.pricePerGB;

    // Legacy model: platform apps were billed once, up front.
    let oneTimeAppCosts = 0;
    const legacyUnavailable = [];
    if (isLegacy) {
        Object.entries(state.selectedPlatforms || {}).forEach(([key, on]) => {
            if (!on || !platforms[key]) return;
            const legacy = APP_CONFIG.legacyAppBundles[key];
            if (!legacy) {
                // Selected, but never part of the one-time catalogue.
                legacyUnavailable.push(platforms[key].name);
                return;
            }
            if (!legacy.included) oneTimeAppCosts += legacy.price;
        });
        oneTimeAppCosts *= (1 - (state.appDiscount || 0) / 100);
    }

    const recurringPlatformCost = isLegacy ? 0 : platformAddOnCost;
    // Usage add-ons are identical on every tier, but they are still part of
    // what the customer pays, so they belong in the total.
    const usageCost = usage ? usage.total : 0;
    const addOnCosts = recurringPlatformCost + featureAddOnCost + usageCost;
    const monthlyRecurringCost = baseCost + addOnCosts + bandwidthOverage + storageOverage;

    const fitsBandwidth = isCustom || requirements.bandwidth <= pkg.bandwidth;
    const fitsStorage = isCustom || requirements.storage <= pkg.storage;
    const fitsSubscribers = isCustom || activeSubscribers <= pkg.activeSubscribers;

    return {
        tierKey,
        isCustom,
        baseCost,
        platformAddOnCost: recurringPlatformCost,
        platformAddOnItems,
        featureAddOnCost,
        featureAddOnItems,
        usageCost,
        usageItems: usage ? usage.items : [],
        bandwidthOverage,
        storageOverage,
        bandwidthOverGB,
        storageOverGB,
        overageCost: bandwidthOverage + storageOverage,
        addOnCosts,
        oneTimeAppCosts,
        legacyUnavailable,
        monthlyRecurringCost,
        totalCost: monthlyRecurringCost + oneTimeAppCosts,
        fitsBandwidth,
        fitsStorage,
        fitsSubscribers,
        canHandle: fitsBandwidth && fitsStorage && fitsSubscribers
    };
};

/**
 * Cheapest tier that can actually serve this audience.
 *
 * Bandwidth and storage are soft limits — going over is billed per GB, so a
 * tier stays a candidate when it needs overage and the cost simply shows up in
 * the total. Subscriber guidance is the one dimension overage cannot buy your
 * way out of, so it is what gates the recommendation.
 */
export const recommendTier = (tierCosts) => {
    const priced = TIER_ORDER.filter(t => !APP_CONFIG.packages[t].custom);
    const bigEnough = priced.filter(t => tierCosts[t].fitsSubscribers);
    if (bigEnough.length === 0) return 'advanced';
    return bigEnough
        .slice()
        .sort((a, b) => tierCosts[a].monthlyRecurringCost - tierCosts[b].monthlyRecurringCost)[0];
};

/**
 * Upgrade-vs-stack comparison, computed from the current basket.
 *
 * The published sheet asserts that upgrading beats stacking add-ons. At a flat
 * $150 per platform that is not always true, so this reports whichever is
 * actually cheaper instead of repeating the claim.
 */
export const computeUpgradeInsight = (tierCosts) => {
    const priced = TIER_ORDER.filter(t => !APP_CONFIG.packages[t].custom);
    // Usage add-ons cost the same on every tier, so they are excluded from this
    // comparison — leaving them in would dilute the difference between tiers.
    const comparable = (t) => tierCosts[t].monthlyRecurringCost - tierCosts[t].usageCost;
    const ranked = priced.slice().sort((a, b) => comparable(a) - comparable(b));
    const cheapest = ranked[0];
    const cheapestCost = comparable(cheapest);

    // Lowest tier that bundles every selected platform and feature.
    const fullyBundled = priced.find(t =>
        tierCosts[t].platformAddOnItems.length === 0 && tierCosts[t].featureAddOnItems.length === 0
    );
    const addOnCount = tierCosts[cheapest].platformAddOnItems.length
        + tierCosts[cheapest].featureAddOnItems.length;

    if (!fullyBundled || fullyBundled === cheapest || addOnCount === 0) {
        return { kind: 'none', cheapest, cheapestCost, addOnCount };
    }

    const bundledCost = tierCosts[fullyBundled].monthlyRecurringCost;
    const delta = bundledCost - cheapestCost;
    return {
        kind: delta > 0 ? 'stack-wins' : 'upgrade-wins',
        cheapest,
        cheapestCost,
        fullyBundled,
        bundledCost,
        delta: Math.abs(delta),
        addOnCount
    };
};

/**
 * Cost the whole quote: requirements, revenue, every tier, and the verdict.
 *
 * `calculationSettings` is the effective calibration (see settingsStore). Omit
 * it to use the compiled defaults.
 */
export const priceQuote = (state, calculationSettings) => {
    const merged = { ...DEFAULT_QUOTE_STATE, ...state };
    const cs = calculationSettings || APP_CONFIG.calculationSettings;
    const requirements = calculateRequirements(merged, cs);
    const revenue = calculateRevenue(merged);
    const activeSubscribers = estimateActiveSubscribers(merged.totalPlays, merged.subscriberEstimationType);
    const usage = calculateUsageAddOns(merged);

    const tierCosts = {};
    TIER_ORDER.forEach(t => {
        tierCosts[t] = calculateTierCosts(t, merged, requirements, activeSubscribers, usage);
    });

    return {
        state: merged,
        // The settings this quote was priced with. Carried through so a shared
        // quote can be re-priced on exactly the calibration that produced it.
        calculationSettings: cs,
        requirements,
        revenue,
        activeSubscribers,
        usage,
        tierCosts,
        recommendedTier: recommendTier(tierCosts),
        upgradeInsight: computeUpgradeInsight(tierCosts)
    };
};

/**
 * Four-quarter projection. Every value is a period total (a quarter is three
 * months), so callers must format these without the yearly multiplier.
 */
export const buildProjections = ({ revenue, tierCosts, monthlyGrowthRate }) => {
    const packages = APP_CONFIG.packages;
    const pricedTiers = TIER_ORDER.filter(t => !packages[t].custom);
    const growthMultiplier = 1 + ((monthlyGrowthRate || 0) / 100);

    const now = new Date();
    const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4'];
    const quarters = [];
    for (let i = 0; i < 4; i++) {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const quarterIndex = (currentQuarter + i) % 4;
        const year = now.getFullYear() + Math.floor((currentQuarter + i) / 4);
        quarters.push({ name: quarterNames[quarterIndex], year, fullName: `${quarterNames[quarterIndex]} ${year}` });
    }

    const quarterly = quarters.map((quarter, index) => {
        const quarterlyRevenue = revenue * Math.pow(growthMultiplier, index * 3) * 3;
        const isFirstQuarter = index === 0;
        const tiers = {};
        pricedTiers.forEach(tierKey => {
            const costs = tierCosts[tierKey];
            const oneTime = isFirstQuarter ? costs.oneTimeAppCosts : 0;
            const totalCost = (costs.monthlyRecurringCost * 3) + oneTime;
            tiers[tierKey] = {
                monthlyCost: costs.monthlyRecurringCost * 3,
                oneTimeCost: oneTime,
                totalCost,
                profit: quarterlyRevenue - totalCost
            };
        });
        return {
            quarter: quarter.fullName,
            quarterShort: quarter.name,
            revenue: quarterlyRevenue,
            isFirstQuarter,
            tiers
        };
    });

    const annual = { revenue: quarterly.reduce((s, p) => s + p.revenue, 0), tiers: {} };
    pricedTiers.forEach(tierKey => {
        annual.tiers[tierKey] = {
            totalCost: quarterly.reduce((s, p) => s + p.tiers[tierKey].totalCost, 0),
            profit: quarterly.reduce((s, p) => s + p.tiers[tierKey].profit, 0)
        };
    });

    return { quarterly, annual, pricedTiers };
};

/** Validated series colors, keyed by tier. See src/theme.css for provenance. */
export const TIER_SERIES_COLOR = {
    starter: '#0f87b2',
    streamer: '#b87d00',
    contentGuru: '#4a3aa7',
    advanced: '#1e1b39'
};
export const REVENUE_SERIES_COLOR = '#1e1b39';

/**
 * Currency formatter for display.
 *
 * Whole amounts show no decimals, but sub-dollar values keep two — the per-GB
 * overage rates are $0.01 and $0.03, and rounding those to whole dollars
 * printed "$0/GB" in the customer-facing quote.
 */
export const makeMoneyFormatter = (currency = 'USD') => (amount) => {
    const abs = Math.abs(amount);
    const decimals = abs > 0 && abs < 1 ? 2 : 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(amount);
};
